import { Inbox, NovuProvider, useNovu, useCounts } from '@novu/react';
import { UserContainer } from '@/providers';
import { useRef, useEffect, useState, useMemo } from 'react';

const ORIGINAL_TITLE = 'ViewMetrics';

/**
 * Updates the document title with unread notification count
 */
const updateDocumentTitle = (count: number) => {
  if (count > 0) {
    const displayCount = count > 99 ? '99+' : count;
    document.title = `(${displayCount}) ${ORIGINAL_TITLE}`;
  } else {
    document.title = ORIGINAL_TITLE;
  }
};

/**
 * Creates a favicon with a notification badge overlay
 */
const createFaviconWithBadge = (
  count: number,
  originalFavicon: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 32;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Draw original favicon
      ctx.drawImage(img, 0, 0, size - 6, size - 6);

      if (count > 0) {
        // Draw a small red dot indicator in the top-right corner
        const dotRadius = 8;
        const dotX = size - dotRadius + 1;
        const dotY = dotRadius - 0.5;

        // Draw red dot
        ctx.fillStyle = '#EF4444'; // red-500
        ctx.beginPath();
        ctx.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI);
        ctx.fill();

        // Draw white border for better visibility
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      reject(new Error('Failed to load favicon'));
    };

    img.src = originalFavicon;
  });
};

/**
 * Updates the favicon with a notification badge
 */
const updateFaviconWithBadge = async (count: number) => {
  try {
    // Get the original favicon - use the .ico file
    const originalFavicon = '/favicon.ico';

    // Create favicon with badge
    const newFavicon = await createFaviconWithBadge(count, originalFavicon);

    // Remove all existing favicon links to avoid conflicts
    const existingLinks = document.querySelectorAll('link[rel*="icon"]');
    existingLinks.forEach(link => {
      if (link.getAttribute('rel') === 'icon') {
        link.remove();
      }
    });

    // Create new favicon link element
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = newFavicon;

    // Append to head
    document.head.appendChild(link);
  } catch (error) {
    // Silently fail if favicon update is not supported
  }
};

/**
 * Play a subtle notification sound using Web Audio API
 */
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();

    // Create a gentle two-tone notification sound
    const createTone = (
      frequency: number,
      startTime: number,
      duration: number
    ) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      // Gentle fade in and out
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const { currentTime } = audioContext;
    createTone(800, currentTime, 0.15); // First tone
    createTone(1000, currentTime + 0.1, 0.15); // Second tone (slightly higher)
  } catch (error) {
    // Silently fail if audio playback is not supported
  }
};

/**
 * Inner component that uses the Novu hook to listen for new notifications
 */
const InboxWithAnimation = ({
  applicationIdentifier,
  userId,
  bellRef,
}: {
  applicationIdentifier: string;
  userId: string;
  bellRef: React.RefObject<HTMLDivElement>;
}) => {
  const novu = useNovu();
  const [showRingAnimation, setShowRingAnimation] = useState(false);

  const { counts } = useCounts({ filters: [{ read: false }] });
  const unreadCount = counts?.[0]?.count ?? 0;

  useEffect(() => {
    updateDocumentTitle(unreadCount);
    updateFaviconWithBadge(unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh the title and favicon to reflect current state
        updateDocumentTitle(unreadCount);
        updateFaviconWithBadge(unreadCount);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [unreadCount]);

  // Play sound and show ring animation when new notification arrives
  useEffect(() => {
    if (!novu) {
      return undefined;
    }

    const handleNewNotification = () => {
      // Play notification sound
      playNotificationSound();

      // Show ring animation
      setShowRingAnimation(true);

      setTimeout(() => {
        setShowRingAnimation(false);
      }, 3000);
    };

    // Listen for new notifications - returns unsubscribe function
    const unsubscribe = novu.on(
      'notifications.notification_received',
      handleNewNotification
    );

    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [novu, bellRef]);

  // Appearance configuration with animations
  const appearance = {
    elements: {
      // Add smooth transitions to notifications
      notification: `
        transition-all duration-300 ease-in-out
        animate-in fade-in slide-in-from-top-2
      `,

      // Highlight unread notifications with a subtle pulse
      notificationDot: 'animate-pulse',

      // Smooth transitions for the popup
      popoverContent: `
        animate-in fade-in-0 zoom-in-95
        data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0
        data-[state=closed]:zoom-out-95
      `,
    },
  };

  // Memoize the bell renderer to ensure it updates when showRingAnimation changes
  const renderBell = useMemo(
    () => (bellCount: number | { total: number }) => {
      // In v3.1.0, bellCount might be a number directly or an object with total
      const count =
        typeof bellCount === 'number'
          ? bellCount
          : (bellCount as any)?.total || 0;

      return (
        <span className="relative flex items-center justify-center cursor-pointer border border-grayscale-200 rounded-full h-8 w-8 flex-shrink-0 overflow-visible">
          {/* Expanding Ring Animation */}
          {showRingAnimation && (
            <span
              className="absolute inset-0 rounded-full border-2 border-red-500 z-0"
              style={{
                animation: 'expandRing 1s ease-out forwards',
              }}
            />
          )}

          {/* Bell Icon */}
          <i className="ti ti-bell text-base text-grayscale-600 relative z-10" />

          {/* Notification Badge */}
          {count > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full z-20">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </span>
      );
    },
    [showRingAnimation]
  );

  return (
    <Inbox
      applicationIdentifier={applicationIdentifier}
      subscriberId={userId}
      appearance={appearance}
      renderBell={renderBell}
    />
  );
};

/**
 * Novu Inbox - Real-time notification center
 *
 * Displays in-app notifications for report generation and other system events
 */
export const NovuInbox = () => {
  const { state: userState } = UserContainer.useContainer();
  const userId = userState?.user?.id;
  const bellRef = useRef<HTMLDivElement>(null);

  // Don't render if user ID is not available or Novu is not configured
  const applicationIdentifier = import.meta.env.VITE_NOVU_APP_ID;

  if (!userId || !applicationIdentifier) {
    return null;
  }

  return (
    <div ref={bellRef}>
      <NovuProvider
        applicationIdentifier={applicationIdentifier}
        subscriberId={userId.toString()}
      >
        <InboxWithAnimation
          applicationIdentifier={applicationIdentifier}
          userId={userId.toString()}
          bellRef={bellRef}
        />
      </NovuProvider>
    </div>
  );
};
